/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2023, Zumper
 */

import { cn } from '@zcode-ui/shadz-ui'
import { createKeyHandler } from '@zcode/a11y-utils'
import { Keys } from '@zcode/utils'
import React, { useCallback } from 'react'
import type { OptionType } from './BaseInputWithPopover'

export interface OpenSelectProps<Opt extends OptionType<unknown>> {
  autoFocus?: boolean
  columns?: 3
  selectedOptionRef: React.MutableRefObject<HTMLButtonElement | null> | undefined
  isMobile: boolean
  onSelect: (option: Opt, index: number) => void
  options: readonly Opt[]
  value: Opt['displayValue'] | null
}

export const OpenSelect = <Opt extends OptionType<unknown>>({
  autoFocus,
  columns,
  selectedOptionRef,
  isMobile,
  onSelect,
  options,
  value,
}: OpenSelectProps<Opt>) => {
  const selected = options.findIndex((option) => option.displayValue === value)
  const onKeyDown = createKeyHandler(
    Keys.ARROW_DOWN,
    Keys.ARROW_LEFT,
    Keys.ARROW_RIGHT,
    Keys.ARROW_UP,
    Keys.ENTER,
    Keys.SPACE
  )(
    useCallback(
      (e: React.KeyboardEvent<HTMLButtonElement>) => {
        e.preventDefault()
        switch (e.which) {
          case Keys.ENTER:
          case Keys.SPACE:
            e.currentTarget.click()
            break
          case Keys.ARROW_DOWN: {
            const { parentElement } = e.currentTarget
            if (parentElement) {
              const siblings = [...parentElement.children]

              const currentIndex = siblings.indexOf(e.currentTarget)
              if (currentIndex < options.length - 1) {
                const next = siblings[Math.min(currentIndex + (columns || 1), options.length - 1)]
                if (next instanceof HTMLElement) {
                  next.focus()
                }
              }
            }
            break
          }
          case Keys.ARROW_UP: {
            const { parentElement } = e.currentTarget
            if (parentElement) {
              const siblings = [...parentElement.children]

              const currentIndex = siblings.indexOf(e.currentTarget)
              if (currentIndex > (columns || 1) - 1) {
                const prev = siblings[currentIndex - (columns || 1)]
                if (prev instanceof HTMLElement) {
                  prev.focus()
                }
              }
            }
            break
          }
          case Keys.ARROW_LEFT:
            if (columns && columns > 1) {
              const prev = e.currentTarget.previousElementSibling
              if (prev instanceof HTMLElement) {
                prev.focus()
              }
            }
            break
          case Keys.ARROW_RIGHT:
            if (columns && columns > 1) {
              const next = e.currentTarget.nextElementSibling
              if (next instanceof HTMLElement) {
                next.focus()
              }
            }
            break
        }
      },
      [columns, options]
    )
  )
  const selectedish = Math.max(0, selected)
  return (
    <>
      {options.map((option, index) => (
        <button
          aria-selected={index === selected}
          // biome-ignore lint/a11y/noAutofocus: I think we need this so that when the MoveInDateInput switches from showing the calendar view to showing the OpenSelect, we keep the focus within the popover
          autoFocus={autoFocus && index === 0}
          className={cn(
            'block focus:bg-gray-100 focus:cursor-pointer hover:bg-gray-100 hover:cursor-pointer px-6 py-2.5 aria-selected:font-bold aria-selected:text-gray-950 text-gray-800 text-left w-full',
            isMobile ? 'body16Md' : 'body14Md',
            option.className
          )}
          key={index}
          onClick={() => onSelect(option, index)}
          onKeyDown={onKeyDown}
          ref={index === selectedish ? selectedOptionRef : undefined}
          role="option"
          tabIndex={index === selectedish ? 0 : -1}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </>
  )
}
